import Column from '@app/components/layout/Column'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { TabsGroup } from '@app/components/core/tabs/Tabs'
import { match } from 'ts-pattern'
import { useParamState } from '@app/util/hooks/useParamState'
import { useQuery } from '@based/sdk/react'
import { Overview } from './tabContent/Overview'
import { Initiatives } from './tabContent/Initiatives'
import { Current } from './tabContent/Current'
import { Thread } from '@app/components/blocks/thread/Thread'
import { Locations } from './tabContent/Locations'
import { StepByStep } from './tabContent/StepByStep'
import { Subsidies } from './tabContent/Subsidies'
import { MunicipalityViewModel } from 'functions/app/geography/view/municipality'
import { KnowledgeHubModulesSection } from '@app/components/blocks/KnowledgeHubModulesSection'
import { isFeatureEnabled, isFeatureAvailable } from '@app/util/featureToggle'
import { municipalityTabs, regionsTabsQueryParamName } from '../constants'
import { RegionalServiceProvidersGrid } from './tabContent/RegionalServiceProvidersGrid'
import { ServiceProviderListRegionalModel } from 'functions/app/service-provider/list-regional'

type Tab = keyof typeof municipalityTabs
const municipalityTabKeys: Tab[] = Object.values(municipalityTabs)

const isTabAvailable = (
  municipality: MunicipalityViewModel,
  tab: Tab,
): boolean => {
  return match(tab)
    .with(municipalityTabs.overview, () => true)
    .with(municipalityTabs.discussion, () => true)
    .with(municipalityTabs.initiatives, () => true)
    .with(municipalityTabs.locations, () => true)
    .with(municipalityTabs.knowledgeHub, (t) =>
      isFeatureAvailable(municipality.featureToggles, t, [
        municipality.knowledgeHubModules,
      ]),
    )
    .with(municipalityTabs.current, (t) =>
      isFeatureAvailable(municipality.featureToggles, t, [
        municipality.stories,
        municipality.news,
        municipality.blogs,
      ]),
    )
    .with(
      municipalityTabs.experts,
      (matchedTab) =>
        (municipality.serviceProviders?.count ?? 0) > 0 &&
        isFeatureEnabled(municipality.featureToggles, matchedTab),
    )
    .with(municipalityTabs.stepByStep, (t) =>
      isFeatureAvailable(municipality.featureToggles, t, [
        municipality.stepByStepPdf,
      ]),
    )
    .otherwise((matchedTab) =>
      isFeatureEnabled(municipality.featureToggles, matchedTab),
    )
}

export const MunicipalityTabContent = ({
  municipality,
}: {
  municipality: MunicipalityViewModel
}) => {
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.regionPage
  const [tab, setTab] = useParamState(
    municipalityTabs,
    regionsTabsQueryParamName,
  )

  const isExpertsFeatureEnabled = isFeatureEnabled(
    municipality.featureToggles,
    municipalityTabs.experts,
  )
  const regionalExpertsCountQuery = useQuery(
    isExpertsFeatureEnabled ? 'list-regional-service-providers' : null,
    {
      municipalityId: municipality.id,
      count: true,
    },
  )
  const expertsCount = regionalExpertsCountQuery.data?.count ?? 0
  const showExpertsTab = isExpertsFeatureEnabled && expertsCount > 0

  const regionalExpertsListQuery = useQuery(
    tab === municipalityTabs.experts && showExpertsTab
      ? 'list-regional-service-providers'
      : null,
    {
      municipalityId: municipality.id,
    },
  )
  const regionalExperts: ServiceProviderListRegionalModel[] =
    regionalExpertsListQuery.data !== undefined
      ? (regionalExpertsListQuery.data as ServiceProviderListRegionalModel[])
      : []

  const getTabLabel = (tabKey: Tab): string => translations[tabKey]

  return (
    <Column alignItems="stretch" gap="2rem">
      <TabsGroup
        selectedTab={tab}
        onSelectTab={setTab}
        tabs={municipalityTabKeys.map((key) => ({
          value: key,
          label: getTabLabel(key),
          isVisible:
            key === municipalityTabs.experts
              ? showExpertsTab
              : isTabAvailable(municipality, key),
        }))}
      />
      {match(tab)
        .with(municipalityTabs.overview, () => (
          <Overview key="overview" entity={municipality} />
        ))
        .with(municipalityTabs.discussion, () => (
          <Thread key="thread" threadId={municipality.thread?.id} />
        ))
        .with(municipalityTabs.initiatives, () => (
          <Initiatives key="initiatives" entity={municipality} />
        ))
        .with(municipalityTabs.locations, () => (
          <Locations key="locations" entity={municipality} />
        ))
        .with(municipalityTabs.knowledgeHub, (t) =>
          isTabAvailable(municipality, t) ? (
            <KnowledgeHubModulesSection
              key="knowledgeHub"
              modules={municipality.knowledgeHubModules ?? []}
            />
          ) : null,
        )
        .with(municipalityTabs.experts, () =>
          showExpertsTab ? (
            <RegionalServiceProvidersGrid
              key="experts"
              serviceProviders={regionalExperts}
            />
          ) : null,
        )
        .with(municipalityTabs.current, () => (
          <Current key="current" region={municipality} />
        ))
        .with(municipalityTabs.stepByStep, (t) =>
          isTabAvailable(municipality, t) ? (
            <StepByStep key="stepByStep" region={municipality} />
          ) : null,
        )
        .with(municipalityTabs.subsidies, () => (
          <Subsidies key="subsidies" region={municipality} />
        ))
        .exhaustive()}
    </Column>
  )
}
